import { MutableRefObject, useRef } from "react";
import { useRerender } from "./useRerender";
import { debounce } from "lodash";

export type Setter<T> = React.Dispatch<React.SetStateAction<T>>;

export type UseControlledStateReturn<T> = [MutableRefObject<T>, Setter<T>, Setter<T>, Setter<T>];

export type UseControlledStateConfig = {
    debounceTime?: number;
    includePartialSetter?: boolean;
}

const defaultConfig: UseControlledStateConfig = {
    debounceTime: 600,
}

export const useControlledState = <T>(initialValue: T, config: Partial<UseControlledStateConfig> = defaultConfig): UseControlledStateReturn<T> => {
    const mergedConfig = { ...defaultConfig, ...config };

    const rerender = useRerender();

    const ref = useRef<T>(initialValue);

    const setValue: Setter<T> = (valueOrSetter: React.SetStateAction<T>) => {
        if (typeof valueOrSetter === 'function') {
            ref.current = (valueOrSetter as (prevState: T) => T)(ref.current);
        } else {
            ref.current = valueOrSetter;
        }
    }

    const setValueAndRerender: Setter<T> = (valueOrSetter: React.SetStateAction<T>) => {
        setValue(valueOrSetter);
        rerender();
    }

    const setValueAndRerenderDebounced = debounce<Setter<T>>(setValueAndRerender, mergedConfig.debounceTime);

    return [ref, setValue, setValueAndRerender, setValueAndRerenderDebounced];
}

export type UserControlledStateObjectReturn<T> = {
    value: MutableRefObject<T>;
    setValue: Setter<T>;
    setValueAndRerender: Setter<T>;
    setValueAndRerenderDebounced: Setter<T>;
}

export const useControlledStateObjectReturn = <T>(initialValue: T, config: Partial<UseControlledStateConfig> = defaultConfig): UserControlledStateObjectReturn<T> => {
    const [value, setValue, setValueAndRerender, setValueAndRerenderDebounced] = useControlledState<T>(initialValue, config);

    return {
        value,
        setValue,
        setValueAndRerender,
        setValueAndRerenderDebounced,
    };
}

export type UseControlledObjectStateReturn<T extends Record<string, any>> = UserControlledStateObjectReturn<T> & {
    setPartial: Setter<Partial<T>>;
    setPartialAndRerender: Setter<Partial<T>>;
}

export const useControlledObjectState = <T extends Record<string, any>>(initialValue: T, config: Partial<UseControlledStateConfig> = defaultConfig): UseControlledObjectStateReturn<T> => {
    const [
        value,
        setValue,
        setValueAndRerender,
        setValueAndRerenderDebounced,
    ] = useControlledState<T>(initialValue, config);

    const setPartial: Setter<Partial<T>> = (partial: Partial<T>) => {
        const newValue = { ...value.current, ...partial };
        setValue(newValue);
    }

    const setPartialAndRerender: Setter<Partial<T>> = (partial: Partial<T>) => {
        const newValue = { ...value.current, ...partial };
        setValueAndRerender(newValue);
    }

    return {
        value,
        setValue,
        setValueAndRerender,
        setValueAndRerenderDebounced,
        setPartial,
        setPartialAndRerender,
    };
}